import { useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { formatCurrency } from '../../lib/utils';

const currentYear = new Date().getFullYear();

export function ExpenseCoveragePanel() {
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showUnclassified, setShowUnclassified] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.expenseCoverage({
        entity_id: 'property_portfolio',
        year,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const s = result?.summary;

  // Collect all unclassified lines across statements.
  const allUnclassified = result?.statements
    ?.flatMap((st) => (st.unclassified_lines || []).map((l) => ({
      ...l,
      property_ref: st.property_ref,
      property_name: st.property_name_raw,
      month: st.month,
    })))
    ?.sort((a, b) => (a.month || '').localeCompare(b.month || '')) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Expense Coverage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Classifies every statement expense by Account field into cleaning, maintenance,
          management fee, or unclassified. Shows what the classification pipeline misses.
        </p>

        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Run
          </Button>
        </div>

        {s && (
          <>
            {/* Summary badges */}
            <div className="flex flex-wrap gap-3">
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Statements</p>
                <p className="text-lg font-semibold">{s.total_statements}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Total expenses</p>
                <p className="text-lg font-semibold">{s.total_expenses}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Cleaning</p>
                <p className="text-lg font-semibold text-blue-600">{s.cleaning_count}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(parseFloat(s.cleaning_total), 'GBP')}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Maintenance</p>
                <p className="text-lg font-semibold text-orange-600">{s.maintenance_count}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(parseFloat(s.maintenance_total), 'GBP')}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Mgmt Fee</p>
                <p className="text-lg font-semibold text-purple-600">{s.management_fee_count}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(parseFloat(s.management_fee_total || '0'), 'GBP')}</p>
              </div>
              <div className="rounded-lg border p-3 min-w-[120px]">
                <p className="text-xs font-medium text-muted-foreground mb-1">Unclassified</p>
                <p className={`text-lg font-semibold ${s.unclassified_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {s.unclassified_count}
                </p>
                {s.unclassified_count > 0 && (
                  <p className="text-xs text-muted-foreground">{formatCurrency(parseFloat(s.unclassified_total), 'GBP')}</p>
                )}
              </div>
            </div>

            {/* Statement reconciliation summary */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-medium text-muted-foreground">Recon vs Total Expenses:</span>
              {s.recon_reconciled > 0 && (
                <Badge variant="outline" className="text-green-700 border-green-300 text-[10px]">
                  {s.recon_reconciled} reconciled
                </Badge>
              )}
              {s.recon_unclassified_present > 0 && (
                <Badge variant="outline" className="text-yellow-700 border-yellow-300 text-[10px]">
                  {s.recon_unclassified_present} unclassified present
                </Badge>
              )}
              {s.recon_mismatch > 0 && (
                <Badge variant="outline" className="text-red-700 border-red-300 text-[10px]">
                  {s.recon_mismatch} mismatch
                </Badge>
              )}
              {s.recon_no_anchor > 0 && (
                <Badge variant="outline" className="text-muted-foreground text-[10px]">
                  {s.recon_no_anchor} no anchor
                </Badge>
              )}
            </div>

            {/* Unclassified detail toggle */}
            {allUnclassified.length > 0 && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUnclassified(!showUnclassified)}
                >
                  {showUnclassified ? 'Hide' : 'Show'} {allUnclassified.length} unclassified line{allUnclassified.length !== 1 ? 's' : ''}
                </Button>

                {showUnclassified && (
                  <div className="mt-3 max-h-[400px] overflow-y-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Month</TableHead>
                          <TableHead className="text-xs">Property</TableHead>
                          <TableHead className="text-xs">Account</TableHead>
                          <TableHead className="text-xs">Description</TableHead>
                          <TableHead className="text-xs text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allUnclassified.map((line, i) => (
                          <TableRow key={i} className="text-xs">
                            <TableCell className="py-1.5 font-mono">{line.month}</TableCell>
                            <TableCell className="py-1.5 max-w-[160px] truncate" title={line.property_name}>
                              {line.property_name || line.property_ref}
                            </TableCell>
                            <TableCell className="py-1.5">
                              <Badge variant="outline" className="text-[10px]">{line.account || '-'}</Badge>
                            </TableCell>
                            <TableCell className="py-1.5 max-w-[200px] truncate" title={line.description}>
                              {line.description || '-'}
                            </TableCell>
                            <TableCell className="py-1.5 text-right font-mono tabular-nums">
                              {formatCurrency(parseFloat(line.amount), 'GBP')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
